import { Api } from "@core/api/api";
import { applicationFilterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { ApplicationDegree, ApplicationLevel, ProgramTraining } from "@domain/program/programDraft";
import { Application, ApplicationFilterOptions, ApplicationVote } from "./application";
import { ApplicationDraft } from "./applicationDraft";
import {
	applicationDraftToDto,
	ApplicationDto,
	applicationFromDto,
	ApplicationVoteDto,
	applicationVoteFromDto,
} from "./dtos";

const APPLICATION_URL = "/applications";

export class ApplicationApi {
	constructor(private readonly api: Api) {}

	async get(id: string): Promise<Application> {
		const result = await this.api.get<ApplicationDto>(`${APPLICATION_URL}?applicationId=${id}`);
		return applicationFromDto(result.data);
	}

	async getApplication(id: string, currentUser?: string): Promise<Application> {
		const result = await this.api.get<ApplicationDto>(`${APPLICATION_URL}?userId=${id}&voterId=${currentUser}`);
		return applicationFromDto(result.data);
	}

	async save(draft: ApplicationDraft): Promise<Application> {
		const result = await this.api.post<ApplicationDto>(APPLICATION_URL, {
			...applicationDraftToDto(draft),
			programSlug: "omat",
		});
		return applicationFromDto(result.data);
	}

	async create(draft: ApplicationDraft): Promise<Application> {
		const result = await this.api.post<ApplicationDto>(`${APPLICATION_URL}/validate`, {
			...applicationDraftToDto(draft),
			programSlug: "omat",
		});
		return applicationFromDto(result.data);
	}

	async getDegreeLevel(): Promise<Array<ApplicationLevel>> {
		const result = await this.api.get<Promise<Array<ApplicationLevel>>>("/levels");
		return result.data;
	}

	async getDegreeProgram(levelId: string): Promise<Array<ApplicationDegree>> {
		const result = await this.api.get<ProgramTraining[]>(`/program_degrees/${levelId}`);
		return result.data;
	}

	async vote(applicationId: string, key?: string): Promise<ApplicationVoteDto> {
		const result = await this.api.post<ApplicationVote>(`${APPLICATION_URL}/${applicationId}/votes`, {
			key: key ?? null,
		});
		return applicationVoteFromDto(result.data);
	}

	async getMyApplication(): Promise<Application> {
		const result = await this.api.get<Application>(`${APPLICATION_URL}?filter=mine`);
		return result.data;
	}

	async shareApplication(id: string, text?: string): Promise<void> {
		return this.api.post(`/applications/${id}/share`, { text: text });
	}

	async listApplications(
		filterOptions: ApplicationFilterOptions | undefined,
		pageOptions: PaginationOptions
	): Promise<Page<Application>> {
		const result = await this.api.get<Page<ApplicationDto>>(
			`${APPLICATION_URL}?${pageParams(pageOptions)}${applicationFilterParams(filterOptions)}`
		);
		return { ...result.data, content: result.data.content.map(applicationFromDto) };
	}
}
